import React, { useRef, useCallback } from 'react';

/**
 * Shared scrollable container that auto-fetches the next page of a react-query
 * useInfiniteQuery once the user scrolls near the bottom — instead of a manual
 * "Load more" button. Works with any pagination scheme since it only needs the
 * generic useInfiniteQuery return values.
 *
 * @param {boolean} hasNextPage
 * @param {boolean} isFetchingNextPage
 * @param {Function} fetchNextPage
 * @param {boolean} [disabled] - extra guard to suppress auto-loading (e.g. while a search is active)
 * @param {number} [maxHeight] - px
 */
export default function InfiniteScrollBox({
  children,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  disabled = false,
  maxHeight = 600,
  className = '',
  style,
}) {
  const scrollRef = useRef(null);
  // Keep a stable ref so the scroll handler always sees fresh state without re-binding.
  const stateRef = useRef({});
  stateRef.current = { hasNextPage, isFetchingNextPage, disabled };

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { hasNextPage: hnp, isFetchingNextPage: fnp, disabled: dis } = stateRef.current;
    if (fnp || !hnp || dis) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 150) {
      fetchNextPage();
    }
  }, [fetchNextPage]);

  return (
    <div
      ref={scrollRef}
      onScroll={handleScroll}
      className={`border rounded overflow-auto ${className}`}
      style={{ maxHeight, ...style }}
    >
      {children}
      {isFetchingNextPage && (
        <div className="text-center py-3">
          <div className="spinner-border spinner-border-sm text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}
    </div>
  );
}
